class BookDownloadSection extends HTMLElement {
  constructor() {
    super();
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onIframeLoad = this.onIframeLoad.bind(this);
    this.pendingBlockId = null;
    this.expectingIframeLoad = false;
    this.pendingTimer = null;
  }

  connectedCallback() {
    this.storageKey = `book-download-unlocked-${this.dataset.sectionId}`;
    this.modal = this.querySelector('[data-book-modal]');
    this.iframe = this.querySelector('[data-book-download-frame]');
    this.bindTriggers();
    this.bindModal();
    this.bindForms();
    this.restoreUnlockedState();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.iframe) this.iframe.removeEventListener('load', this.onIframeLoad);
    if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
  }

  bindTriggers() {
    this.querySelectorAll('[data-book-open-modal]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.openModal(button.dataset.blockId);
      });
    });
  }

  bindModal() {
    if (!this.modal) return;

    this.modal.querySelectorAll('[data-book-close-modal]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeModal();
      });
    });
  }

  bindForms() {
    if (this.iframe) {
      this.iframe.addEventListener('load', this.onIframeLoad);
    }

    this.querySelectorAll('form.book-download__form').forEach((form) => {
      form.setAttribute('novalidate', 'novalidate');

      if (this.iframe && this.iframe.getAttribute('name')) {
        form.setAttribute('target', this.iframe.getAttribute('name'));
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.onFormSubmit(form);
      });
    });
  }

  getUnlockedIds() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || '[]');
    } catch (error) {
      return [];
    }
  }

  setUnlockedId(blockId) {
    const unlocked = this.getUnlockedIds();
    if (!unlocked.includes(blockId)) {
      unlocked.push(blockId);
      localStorage.setItem(this.storageKey, JSON.stringify(unlocked));
    }
  }

  restoreUnlockedState() {
    this.getUnlockedIds().forEach((blockId) => this.unlockBlock(blockId, false));
  }

  openModal(blockId) {
    if (!this.modal || !blockId) return;

    this.modal.querySelectorAll('[data-book-panel]').forEach((panel) => {
      const isActive = panel.dataset.blockId === blockId;
      panel.hidden = !isActive;

      if (isActive) {
        this.togglePanelState(panel, this.getUnlockedIds().includes(blockId));
      }
    });

    this.modal.classList.remove('is-closing');
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('promo-popup--open');
    document.addEventListener('keydown', this.onKeyDown);

    const activePanel = this.modal.querySelector(`[data-book-panel][data-block-id="${blockId}"]`);
    const emailInput = activePanel && activePanel.querySelector('input[type="email"]');
    const successVisible = activePanel && activePanel.querySelector('[data-book-success]:not([hidden])');

    if (emailInput && !successVisible) {
      window.setTimeout(() => emailInput.focus(), 50);
    }
  }

  closeModal() {
    if (!this.modal || !this.modal.classList.contains('is-open')) return;

    this.modal.classList.add('is-closing');
    document.removeEventListener('keydown', this.onKeyDown);

    window.setTimeout(() => {
      this.modal.classList.remove('is-open', 'is-closing');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('promo-popup--open');
    }, 320);
  }

  onKeyDown(event) {
    if (event.key === 'Escape') this.closeModal();
  }

  togglePanelState(panel, unlocked) {
    const form = panel.querySelector('form.book-download__form');
    const success = panel.querySelector('[data-book-success]');
    if (!form || !success) return;

    form.hidden = Boolean(unlocked);
    success.hidden = !unlocked;
  }

  unlockBlock(blockId, persist = true) {
    if (!blockId) return;
    if (persist) this.setUnlockedId(blockId);

    const panel = this.querySelector(`[data-book-panel][data-block-id="${blockId}"]`);
    if (panel) this.togglePanelState(panel, true);

    this.querySelectorAll(`[data-book-open-modal][data-block-id="${blockId}"]`).forEach((button) => {
      const downloadUrl = button.dataset.downloadUrl;
      if (!downloadUrl) return;

      const link = document.createElement('a');
      link.className = button.className;
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = button.dataset.unlockedLabel || button.textContent;
      button.replaceWith(link);
    });
  }

  finishPending(success) {
    const blockId = this.pendingBlockId;
    const form = this.pendingForm;

    if (this.pendingTimer) {
      window.clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }

    this.expectingIframeLoad = false;
    this.pendingBlockId = null;
    this.pendingForm = null;

    if (form) {
      form.dataset.submitting = 'false';
      const submitButton = form.querySelector('[data-book-submit]');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        if (this.pendingButtonLabel != null) {
          submitButton.innerHTML = this.pendingButtonLabel;
        }
      }
    }

    this.pendingButtonLabel = null;

    if (success && blockId) {
      this.unlockBlock(blockId, true);
    }
  }

  onIframeLoad() {
    if (!this.expectingIframeLoad || !this.pendingBlockId) return;
    this.finishPending(true);
  }

  onFormSubmit(form) {
    if (!form || form.dataset.submitting === 'true') return;

    const panel = form.closest('[data-book-panel]');
    const blockId = panel && panel.dataset.blockId;
    const emailInput = form.querySelector('input[type="email"]');
    const message = form.querySelector('[data-book-form-message]');
    const submitButton = form.querySelector('[data-book-submit]');

    if (!blockId || !emailInput) return;

    const email = emailInput.value.trim();
    if (!email || !emailInput.checkValidity()) {
      emailInput.reportValidity();
      this.showMessage(message, 'Please enter a valid email address.', true);
      return;
    }

    form.dataset.submitting = 'true';
    this.pendingButtonLabel = submitButton ? submitButton.innerHTML : '';

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    this.showMessage(message, '', false);
    this.pendingBlockId = blockId;
    this.pendingForm = form;
    this.expectingIframeLoad = true;

    if (this.iframe && this.iframe.getAttribute('name')) {
      form.setAttribute('target', this.iframe.getAttribute('name'));
    }

    // Native Shopify customer form POST (creates the customer in admin).
    // Target iframe keeps the main page from reloading.
    HTMLFormElement.prototype.submit.call(form);

    // Fallback if iframe load event is delayed/blocked.
    this.pendingTimer = window.setTimeout(() => {
      if (this.pendingBlockId === blockId) {
        this.finishPending(true);
      }
    }, 2000);
  }

  showMessage(element, text, isError) {
    if (!element) return;
    element.hidden = !text;
    element.textContent = text || '';
    element.classList.toggle('book-download__form-message--error', Boolean(isError));
  }
}

customElements.get('book-download-section') || customElements.define('book-download-section', BookDownloadSection);
